/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LockOpenTwoToneIcon from "@mui/icons-material/LockOpenTwoTone";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "./styles.css";
import {
  clearLocalStorageData,
  getLocalStorageData,
} from "../../../../shared/utils/localStorage";
import useMutation from "../../../../shared/components/hooks/useMutation";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Link } from "react-router-dom";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { useSosSocket } from "../../../../contexts/SosSocketContext";
import { useNavigate } from "react-router-dom";

const UserBoxLabel = styled(Typography)(
  ({ theme }) => `
    font-weight: ${theme.typography.fontWeightBold};
    color: ${theme.palette.secondary.main};
    display: block;
    margin: 5px;
`,
);

function HeaderUserBox() {
  const [, setSessionVersion] = useState(0);
  const localStorageData = getLocalStorageData("userData");
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const refreshSession = () => setSessionVersion((version) => version + 1);
    window.addEventListener("userDataUpdated", refreshSession);
    window.addEventListener("storage", refreshSession);
    return () => {
      window.removeEventListener("userDataUpdated", refreshSession);
      window.removeEventListener("storage", refreshSession);
    };
  }, []);
  const { modifyData } = useMutation();
  const { sosTriggered, clearSos } = useSosSocket();
  const navigate = useNavigate();

  const isSuperAdmin = localStorageData?.userType === "SuperAdmin";
  const hideNotificationsForSuperAdmin = isSuperAdmin;
  const {
    loading,
    data: empData,
    fetchData,
  } = !isSuperAdmin &&
  localStorageData?.employeeID != null &&
  localStorageData?.employee?.orgID ===
    localStorageData?.employee?.organization?.id
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery(`/user-profile/user`, null)
    : { loading: false, data: null, fetchData: () => {} };

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async () => {
    const response = await modifyData(`/admin/logout`, "post");
    if (response) {
      await clearLocalStorageData();
      window.location.href = "/login";
    }
  };

  const name = localStorageData?.employee?.name
    ? localStorageData?.employee?.name
    : localStorageData?.email
      ? localStorageData?.email
      : "";

  const userImage = empData?.data?.profileInput?.image;

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* SOS Warning Icon */}
        {sosTriggered && !hideNotificationsForSuperAdmin && (
          <Tooltip title="SOS Alert received! Click to dismiss" arrow>
            <WarningAmberIcon
              onClick={() => {
                clearSos();
                navigate("/dashboard");
              }}
              sx={{
                color: "#f44336",
                cursor: "pointer",
                animation: "pulse 1s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                  "100%": { opacity: 1 },
                },
              }}
            />
          </Tooltip>
        )}

        <Box className="userBox" ref={ref} onClick={handleOpen}>
          <Box className="UserBoxText">
            <UserBoxLabel>
              <span className="adminName">{name}</span>
            </UserBoxLabel>
          </Box>
          <Box>
            {userImage ? (
              <Avatar
                alt={name}
                src={userImage}
                className="adminAvatar"
                sx={{ width: 20, height: 20 }}
              />
            ) : (
              <AccountCircleIcon className="adminIcon" />
            )}
          </Box>
        </Box>
      </Box>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 60,
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: 210,
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: "-23px",
            },
          },
        }}
      >
        {localStorageData?.userType === "SuperAdmin" ||
        localStorageData?.employeeID === null ? (
          <Box className="MenuUserBox" display="flex">
            {userImage ? (
              <Avatar
                alt={name}
                src={userImage}
                sx={{ width: 20, height: 20, marginRight: 1 }}
              />
            ) : (
              <AccountCircleIcon />
            )}
            <Typography variant="body2" className="profileName">
              {name}
            </Typography>
          </Box>
        ) : (
          <Link
            to={`/profile/view`}
            style={{ textDecoration: "none", color: "inherit" }}
            onClick={handleClose}
          >
            <Box className="MenuUserBox" display="flex">
              {userImage ? (
                <Avatar
                  alt={name}
                  src={userImage}
                  sx={{ width: 20, height: 20, marginRight: 1 }}
                />
              ) : (
                <AccountCircleIcon />
              )}
              <Typography variant="body2" className="profileName">
                {name}
              </Typography>
            </Box>
          </Link>
        )}
        <Divider />
        <Box className="Box">
          <Button
            color="primary"
            fullWidth
            onClick={() => {
              handleLogout();
            }}
          >
            <LockOpenTwoToneIcon className="LockOpenTwoToneIcon" />
            Sign out
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserBox;
